/**
 * Admin → Denials.
 *
 * Auth/authorization rejections (whitelist miss, expired JWT, disabled user,
 * etc). Color-coded by reason for quick scanning.
 */
import { useState } from "react";
import { clsx } from "clsx";

import { adminApi } from "../../api/admin";
import { PageHeader } from "../../components/PageHeader";
import { QueryBoundary } from "../../components/QueryBoundary";
import { formatRelative } from "../../lib/formatRelative";

/**
 * Reason → severity bucket.
 * - alert: high-signal (disabled user, scope violation)
 * - warn:  rejected request, security event (whitelist miss)
 * - info:  recoverable (expired JWT, missing token)
 */
function severityOf(reason: string): "alert" | "warn" | "info" {
  const r = reason.toLowerCase();
  if (
    r.includes("user_disabled") ||
    r.includes("scope") ||
    r.includes("revoked")
  ) {
    return "alert";
  }
  if (
    r.includes("whitelist") ||
    r.includes("not_authorized") ||
    r.includes("forbidden")
  ) {
    return "warn";
  }
  return "info";
}

export function DenialsPage() {
  const [since, setSince] = useState("");
  const [emailFilter, setEmailFilter] = useState("");

  const query = adminApi.denials.query({
    since: since || undefined,
    attempted_email: emailFilter || undefined,
    limit: 200,
  });

  return (
    <>
      <PageHeader
        title="Denials"
        description="Authorization rejections. Stripe color signals severity — red is something you should look at."
      />

      <div className="flex items-end gap-4 mb-5 flex-wrap">
        <div>
          <label className="label" htmlFor="denials-since">
            Since
          </label>
          <input
            id="denials-since"
            className="input"
            type="date"
            style={{ minWidth: 160 }}
            value={since}
            onChange={(e) => setSince(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="denials-email">
            Attempted email
          </label>
          <input
            id="denials-email"
            className="input"
            placeholder="filter by email…"
            style={{ minWidth: 220 }}
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
          />
        </div>
        <div className="ml-auto text-sm text-ink-low pb-2">
          <span className="tnum font-medium text-ink">
            {query.data?.length ?? 0}
          </span>{" "}
          entries
        </div>
      </div>

      <QueryBoundary
        query={query}
        empty={
          <div className="empty-state">
            <div className="title">No denials</div>
            <p className="hint">
              Quiet here — no auth rejections in the selected window.
            </p>
          </div>
        }
        isEmpty={(d) => d.length === 0}
      >
        {(rows) => (
          <div className="panel panel--flush overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Reason</th>
                  <th>Attempted email</th>
                  <th>Source IP</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const severity = severityOf(row.reason);
                  return (
                    <tr
                      key={row.id}
                      className={clsx(`row-severity-${severity}`)}
                    >
                      <td className="text-sm text-ink-low tnum">
                        {formatRelative(row.created_at)}
                      </td>
                      <td>
                        <span className="font-mono text-sm text-ink">
                          {row.reason}
                        </span>
                      </td>
                      <td className="text-sm text-ink-medium">
                        {row.attempted_email || "—"}
                      </td>
                      <td className="text-xs text-ink-low font-mono">
                        {row.source_ip || "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}
